import { DateTimeService } from "../../services/date-time-service"
import { CalendarEventViewModel } from "../api/calendar-event"
import { NextOccurrence } from "../api/calendar-event-response"
import { TimeZoneType } from "../../constants/timezones"

type PartitionedEvents = {
  oneTimeEvents: CalendarEventViewModel[]
  recurringParentEvents: CalendarEventViewModel[]
  recurringInstanceEvents: CalendarEventViewModel[]
}

const partitionEvents = (events: CalendarEventViewModel[]): PartitionedEvents => {
  return events.reduce<PartitionedEvents>(
    (acc, event) => {
      if (event.isDeletedRecurringEvent) return acc

      if (event.isRecurringInstance) {
        acc.recurringInstanceEvents.push(event)
      } else if (event.isRecurringParent) {
        acc.recurringParentEvents.push(event)
      } else {
        acc.oneTimeEvents.push(event)
      }

      return acc
    },
    {
      oneTimeEvents: [],
      recurringParentEvents: [],
      recurringInstanceEvents: [],
    }
  )
}

const getEventStartDate = (
  event: Pick<CalendarEventViewModel, "startDate">,
  dateTimeService: DateTimeService
): Date => {
  return dateTimeService.removeTimezoneOffset(new Date(event.startDate))
}

const getEventEndDate = (event: Pick<CalendarEventViewModel, "endDate">, dateTimeService: DateTimeService): Date => {
  return dateTimeService.removeTimezoneOffset(new Date(event.endDate))
}

const isVisibleInHomeView = (event: CalendarEventViewModel, dateTimeService: DateTimeService): boolean => {
  const eventEndDate = getEventEndDate(event, dateTimeService)
  return dateTimeService.startOfDay(new Date()) <= eventEndDate
}

const createDisplayEventFromNextOccurrence = (
  parentEvent: CalendarEventViewModel,
  nextOccurrence: NextOccurrence
): CalendarEventViewModel => {
  return {
    ...parentEvent,
    startDate: nextOccurrence.startDate,
    endDate: nextOccurrence.endDate,
    eventLength: nextOccurrence.eventLength,
    parentEventId: parentEvent.id,
  }
}

const groupRecurringInstancesByParentId = (
  recurringInstanceEvents: CalendarEventViewModel[]
): Map<string, CalendarEventViewModel[]> => {
  return recurringInstanceEvents.reduce((acc, instanceEvent) => {
    const existingEvents = acc.get(instanceEvent.parentEventId!) ?? []
    existingEvents.push(instanceEvent)
    acc.set(instanceEvent.parentEventId!, existingEvents)
    return acc
  }, new Map<string, CalendarEventViewModel[]>())
}

const sortEventsByClosestUpcoming = (
  events: CalendarEventViewModel[],
  dateTimeService: DateTimeService
): CalendarEventViewModel[] => {
  return [...events].sort((a, b) => {
    const aStart = getEventStartDate(a, dateTimeService).getTime()
    const bStart = getEventStartDate(b, dateTimeService).getTime()
    return aStart - bStart
  })
}

const getEarliestVisibleInstance = (
  instanceEvents: CalendarEventViewModel[],
  dateTimeService: DateTimeService
): CalendarEventViewModel | null => {
  const visibleInstanceEvents = instanceEvents.filter((event) => isVisibleInHomeView(event, dateTimeService))

  if (visibleInstanceEvents.length === 0) return null

  return sortEventsByClosestUpcoming(visibleInstanceEvents, dateTimeService)[0] ?? null
}

const findEditedNextOccurrenceInstance = (
  instanceEventsInSameSeries: CalendarEventViewModel[],
  nextOccurrence: NextOccurrence
): CalendarEventViewModel | null => {
  return (
    instanceEventsInSameSeries.find((instanceEvent) => {
      return instanceEvent.eventLength === nextOccurrence.eventLength
    }) ?? null
  )
}

const getDisplayEventForRecurringSeries = (
  parentEvent: CalendarEventViewModel | undefined,
  instanceEventsInSameSeries: CalendarEventViewModel[],
  dateTimeService: DateTimeService
): CalendarEventViewModel | null => {
  const earliestVisibleInstance = getEarliestVisibleInstance(instanceEventsInSameSeries, dateTimeService)
  if (!parentEvent?.nextOccurrence) return earliestVisibleInstance

  const nextOccurrence = parentEvent.nextOccurrence
  const editedNextOccurrenceInstance = findEditedNextOccurrenceInstance(instanceEventsInSameSeries, nextOccurrence)
  const candidateEvent = editedNextOccurrenceInstance
    ? editedNextOccurrenceInstance
    : createDisplayEventFromNextOccurrence(parentEvent, nextOccurrence)

  const isCandidateEventVisible = isVisibleInHomeView(candidateEvent, dateTimeService)
  return isCandidateEventVisible ? candidateEvent : earliestVisibleInstance
}

const getRecurringSeriesEventsToDisplay = (
  recurringParentEvents: CalendarEventViewModel[],
  recurringInstanceEvents: CalendarEventViewModel[],
  dateTimeService: DateTimeService
): CalendarEventViewModel[] => {
  const parentEventById = new Map(recurringParentEvents.map((parentEvent) => [parentEvent.id, parentEvent] as const))
  const instanceEventsByParentId = groupRecurringInstancesByParentId(recurringInstanceEvents)

  const recurringSeriesParentIds = new Set<string>([
    ...recurringParentEvents.map((parentEvent) => parentEvent.id),
    ...recurringInstanceEvents.map((instanceEvent) => instanceEvent.parentEventId!),
  ])

  const recurringSeriesEventsToDisplay: CalendarEventViewModel[] = []

  recurringSeriesParentIds.forEach((parentId) => {
    const parentEvent = parentEventById.get(parentId)
    const instanceEventsInSameSeries = instanceEventsByParentId.get(parentId) ?? []

    const eventToDisplay = getDisplayEventForRecurringSeries(parentEvent, instanceEventsInSameSeries, dateTimeService)

    if (eventToDisplay) {
      recurringSeriesEventsToDisplay.push(eventToDisplay)
    }
  })

  return recurringSeriesEventsToDisplay
}

export const getEventsToDisplayInHomeView = (
  events: CalendarEventViewModel[],
  timeZone: TimeZoneType
): CalendarEventViewModel[] => {
  const dateTimeService = new DateTimeService({ timeZone })
  const { oneTimeEvents, recurringParentEvents, recurringInstanceEvents } = partitionEvents(events)

  const visibleOneTimeEvents = oneTimeEvents.filter((event) => isVisibleInHomeView(event, dateTimeService))
  const recurringSeriesEventsToDisplay = getRecurringSeriesEventsToDisplay(
    recurringParentEvents,
    recurringInstanceEvents,
    dateTimeService
  )

  return [...visibleOneTimeEvents, ...recurringSeriesEventsToDisplay]
}
